// 主页入口文件 - 使用单一组件进行响应式设计

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Dialog,
  IconButton,
  Button,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";
import EventIcon from "@mui/icons-material/Event";
import ShareIcon from "@mui/icons-material/Share";
import Divider from "@mui/material/Divider";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ExploreIcon from "@mui/icons-material/Explore";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 简约毛玻璃卡片组件
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12)",
    border: "1px solid rgba(255, 255, 255, 0.25)",
  },
}));

// 毛玻璃效果弹窗
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: 24,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    padding: theme.spacing(3),
    maxWidth: 500,
    margin: theme.spacing(2),
  },
}));

// 事件卡片组件 - 重新设计
const EventCard = ({ event, onClick }) => {
  const [countdown, setCountdown] = useState(null);

  // 计算倒计时
  useEffect(() => {
    if (!event || !event.time) {
      return;
    }

    const calculateCountdown = () => {
      const isValidDate = !isNaN(new Date(event.time).getTime());
      if (!isValidDate) {
        console.error("无效的时间格式:", event.time);
        setCountdown(null);
        return;
      }

      const now = new Date();
      const eventTime = new Date(event.time);
      const diff = eventTime - now;

      if (diff <= 0) {
        setCountdown(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      // 根据用户需求：
      // 如果超过一天：显示天和小时
      // 如果大于一个小时（但不超过一天）：显示小时和分钟
      // 如果小于一个小时：只显示分钟
      if (days > 0) {
        setCountdown({ days, hours });
      } else if (hours > 0) {
        setCountdown({ hours, minutes });
      } else {
        setCountdown({ minutes });
      }
    };

    calculateCountdown();
    const timer = setInterval(calculateCountdown, 1000);
    return () => clearInterval(timer);
  }, [event]);

  // 获取要显示的倒计时单位（可能是一个或两个单位）
  const getDisplayCountdown = () => {
    if (!countdown) return null;
    return countdown; // 直接返回整个countdown对象，可能包含多个单位
  };

  // 获取单位的中文名称
  const getUnitName = (unit) => {
    const unitNames = {
      days: "天",
      hours: "时",
      minutes: "分",
      seconds: "秒",
    };
    return unitNames[unit] || unit;
  };

  const displayCountdown = getDisplayCountdown();

  return (
    <Card
      onClick={onClick}
      sx={{
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(15px)",
        WebkitBackdropFilter: "blur(15px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        cursor: "pointer",
        height: "auto",
        minHeight: "140px",
        width: "100%",
        maxWidth: "500px",
        display: "flex",
        "&:hover": {
          boxShadow: "0 10px 36px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
        },
        "&:active": {
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2.5,
          width: "100%",
          height: "100%",
          gap: 1.5,
        }}
      >
        {/* 标题 */}
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: "rgba(255, 255, 255, 0.95)",
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {event.title}
        </Typography>

        {/* 描述 - 2-3行 */}
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 400,
            color: "rgba(255, 255, 255, 0.85)",
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
            mb: 1,
          }}
        >
          {event.description}
        </Typography>

        {/* 时间和倒计时区域 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "rgba(255, 255, 255, 0.8)",
            }}
          >
            {event.displayTime}
          </Typography>

          {displayCountdown && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "rgba(255, 255, 255, 0.75)",
                }}
              >
                还剩
              </Typography>
              {Object.entries(displayCountdown).map(([unit, value]) => (
                <Box
                  key={unit}
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "rgba(255, 255, 255, 0.95)",
                      lineHeight: 1,
                    }}
                  >
                    {value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "rgba(255, 255, 255, 0.75)",
                    }}
                  >
                    {getUnitName(unit)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// 可点击链接样式
const ClickableLink = styled(Link)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.9)",
  textDecoration: "none",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "block",
  padding: theme.spacing(0.5, 0),
  "&:hover": {
    color: "rgba(255, 255, 255, 1)",
    textDecoration: "underline",
    textDecorationThickness: "1.5px",
    textUnderlineOffset: "3px",
  },
}));

const Home = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [devotionals, setDevotionals] = useState({
    gospel: null,
    devotional: null,
  });
  const [visionSpaces, setVisionSpaces] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [eventsRes, devotionalsRes, visionSpacesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/events`),
          axios.get(`${API_BASE_URL}/devotionals`),
          axios.get(`${API_BASE_URL}/vision-space`),
        ]);

        setEvents(eventsRes.data);

        const latestDevotional = devotionalsRes.data
          .filter((item) => item.type === "devotional")
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        const latestGospel = devotionalsRes.data
          .filter((item) => item.type === "gospel")
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        setDevotionals({
          gospel: latestGospel,
          devotional: latestDevotional,
        });

        setVisionSpaces(visionSpacesRes.data);
      } catch (error) {
        console.error("获取数据失败:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 获取所有唯一的异象空间类型
  const getUniqueVisionTypes = () => {
    const types = visionSpaces.map((space) => space.type);
    const uniqueTypes = [...new Set(types)];
    // 将清晨灵修事工移到最前面
    const sortedTypes = uniqueTypes.sort((a, b) => {
      if (a === "清晨灵修事工") return -1;
      if (b === "清晨灵修事工") return 1;
      return a.localeCompare(b);
    });
    return sortedTypes;
  };

  const formatDate = (date) => {
    return (
      new Date(date).toLocaleString("zh-CN", {
        timeZone: "America/Los_Angeles",
        month: "long",
        day: "numeric",
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
      }) + " PST"
    );
  };

  // 添加日历添加功能
  const addToCalendar = () => {
    const eventTitle = encodeURIComponent(selectedEvent.title);
    const eventDesc = encodeURIComponent(selectedEvent.description);
    const eventStart = new Date(selectedEvent.time)
      .toISOString()
      .replace(/-|:|\.\d+/g, "");
    const eventEnd = new Date(
      new Date(selectedEvent.time).getTime() + 2 * 60 * 60 * 1000
    )
      .toISOString()
      .replace(/-|:|\.\d+/g, "");

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&details=${eventDesc}&dates=${eventStart}/${eventEnd}`;
    window.open(googleCalendarUrl, "_blank");
  };

  // 添加分享功能
  const shareEvent = () => {
    if (navigator.share) {
      navigator
        .share({
          title: selectedEvent.title,
          text: `${selectedEvent.title} - ${selectedEvent.displayTime}\n${selectedEvent.description}`,
          url: window.location.href,
        })
        .catch((error) => console.log("分享失败", error));
    } else {
      const shareText = `${selectedEvent.title} - ${selectedEvent.displayTime}\n${selectedEvent.description}`;
      navigator.clipboard
        .writeText(shareText)
        .then(() => alert("活动信息已复制到剪贴板"))
        .catch((err) => console.error("复制失败:", err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        padding: { xs: "20px 16px 40px", md: "40px 80px 40px" },
        display: "flex",
        flexDirection: "column",
        background: "transparent",
        position: "relative",
        scrollMarginTop: "80px",
      }}
    >
      {/* 主标题区域 - 企业化设计 */}
      <Box
        sx={{
          width: "100%",
          textAlign: "left",
          mb: { xs: 5, md: 7 },
          mt: { xs: 4, md: 6 },
          position: "relative",
        }}
      >
        {/* 主标题 */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            fontWeight: 600,
            color: "rgba(255, 255, 255, 0.95)",
            mb: { xs: 2, md: 2.5 },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
            position: "relative",
            maxWidth: { xs: "100%", md: "800px" },
          }}
        >
          欢迎来到3CCA基督徒平台
        </Typography>

        {/* 装饰线 */}
        <Box
          sx={{
            width: { xs: "50px", md: "70px" },
            height: "3px",
            background: "rgba(255, 255, 255, 0.4)",
            borderRadius: "2px",
            mb: { xs: 3, md: 4 },
          }}
        />

        {/* 副标题区域 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, md: 2 },
            alignItems: "flex-start",
            maxWidth: { xs: "100%", md: "700px" },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1rem", md: "1.15rem" },
              color: "rgba(255, 255, 255, 0.85)",
              lineHeight: 1.7,
              fontWeight: 400,
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              letterSpacing: "0.01em",
            }}
          >
            在这里，我们将遇见基督，践行召命，生命更新
          </Typography>
        </Box>
      </Box>

      {/* 最新动态区域 - 左右两列布局 */}
      <Box sx={{ mt: 4 }}>
        {/* 标题占据全部宽度 */}
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "1.5rem", md: "2rem" },
            fontWeight: 600,
            color: "rgba(255, 255, 255, 0.9)",
            mb: 4,
            textAlign: "left",
            width: "100%",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          "最新动态"：就是即将实时播出的内容
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: { xs: 0, md: 6 },
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          {/* 左侧：事件卡片竖着排列，靠左 */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "0 0 auto" },
              maxWidth: { xs: "100%", md: "500px" },
            }}
          >
            {/* 事件卡片竖着排列 */}
            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                  加载中...
                </Typography>
              </Box>
            ) : events.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {events.slice(0, 5).map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={() => setSelectedEvent(event)}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                  暂无活动数据
                </Typography>
              </Box>
            )}
          </Box>

          {/* 右侧：链接列表 - 企业化设计，固定位置 */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "0 0 auto" },
              display: "flex",
              flexDirection: "column",
              pt: { xs: 4, md: 0 },
              alignItems: { xs: "flex-start", md: "flex-end" },
              position: { xs: "relative", md: "sticky" },
              top: { xs: "auto", md: "120px" },
              alignSelf: { xs: "flex-start", md: "flex-start" },
              maxHeight: { xs: "none", md: "calc(100vh - 140px)" },
              zIndex: 10,
              minWidth: { xs: "auto", md: "180px" },
            }}
          >
            {/* 分组标题 */}
            <Typography
              sx={{
                fontSize: { xs: "0.75rem", md: "0.8rem" },
                fontWeight: 500,
                color: "rgba(255, 255, 255, 0.6)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: { xs: 2, md: 2.5 },
                textAlign: "right",
                width: "100%",
              }}
            >
              快速导航
            </Typography>

            {/* 3CCA崇拜 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mb: { xs: 2, md: 2.5 },
                width: "100%",
              }}
            >
              <ClickableLink
                component="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/worship");
                }}
                sx={{
                  textAlign: "right",
                  border: "none",
                  background: "none",
                  padding: 0,
                  fontFamily: "inherit",
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  fontWeight: 400,
                  color: "rgba(255, 255, 255, 0.85)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  transition: "color 0.15s ease",
                  letterSpacing: "0.01em",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  "&:hover": {
                    color: "rgba(255, 255, 255, 1)",
                  },
                }}
              >
                <MenuBookIcon sx={{ fontSize: "1rem", opacity: 0.7 }} />
                3CCA崇拜
              </ClickableLink>
            </Box>

            {/* 分隔线 */}
            <Divider
              sx={{
                mb: { xs: 2, md: 2.5 },
                borderColor: "rgba(255, 255, 255, 0.15)",
                width: "100%",
              }}
            />

            {/* 异象空间分组标题 */}
            <Typography
              sx={{
                fontSize: { xs: "0.75rem", md: "0.8rem" },
                fontWeight: 500,
                color: "rgba(255, 255, 255, 0.6)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: { xs: 2, md: 2.5 },
                textAlign: "right",
                width: "100%",
              }}
            >
              异象空间
            </Typography>

            {/* 异象空间类型 */}
            {getUniqueVisionTypes().map((type, index) => (
              <Box
                key={type}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                  mb:
                    index < getUniqueVisionTypes().length - 1
                      ? { xs: 2, md: 2.5 }
                      : 0,
                  width: "100%",
                }}
              >
                <ClickableLink
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/vision#${encodeURIComponent(type)}`);
                  }}
                  sx={{
                    textAlign: "right",
                    border: "none",
                    background: "none",
                    padding: 0,
                    fontFamily: "inherit",
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                    fontWeight: 400,
                    color: "rgba(255, 255, 255, 0.85)",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    transition: "color 0.15s ease",
                    letterSpacing: "0.01em",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    "&:hover": {
                      color: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <ExploreIcon sx={{ fontSize: "1rem", opacity: 0.7 }} />
                  {type}
                </ClickableLink>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 毛玻璃效果弹窗 */}
      <StyledDialog
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <Box sx={{ position: "relative", p: 3 }}>
            {/* 分享按钮 */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                shareEvent();
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "rgba(0, 0, 0, 0.6)",
                bgcolor: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                },
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                width: 36,
                height: 36,
              }}
            >
              <ShareIcon />
            </IconButton>

            {/* 标题 */}
            <Typography
              sx={{
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                fontWeight: 700,
                color: "#000",
                textAlign: "center",
                mb: 3,
              }}
            >
              {selectedEvent.title}
            </Typography>

            {/* 倒计时显示 - 简化版 */}
            {(() => {
              const now = new Date();
              const eventTime = new Date(selectedEvent.time);
              const diff = eventTime - now;

              if (diff <= 0) return null;

              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
              const hours = Math.floor(
                (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
              );
              const minutes = Math.floor(
                (diff % (1000 * 60 * 60)) / (1000 * 60)
              );

              let displayCountdown;
              // 根据用户需求：
              // 如果超过一天：显示天和小时
              // 如果大于一个小时（但不超过一天）：显示小时和分钟
              // 如果小于一个小时：只显示分钟
              if (days > 0) {
                displayCountdown = { days, hours };
              } else if (hours > 0) {
                displayCountdown = { hours, minutes };
              } else {
                displayCountdown = { minutes };
              }

              const getUnitName = (unit) => {
                const unitNames = {
                  days: "天",
                  hours: "时",
                  minutes: "分",
                  seconds: "秒",
                };
                return unitNames[unit] || unit;
              };

              return (
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(0,0,0,0.6)",
                      fontWeight: 500,
                    }}
                  >
                    还剩:
                  </Typography>

                  {Object.entries(displayCountdown).map(([unit, value]) => (
                    <Box
                      key={unit}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 1,
                          background: "rgba(0,0,0,0.8)",
                          color: "white",
                          fontSize: "1rem",
                          fontWeight: 700,
                          px: 1,
                        }}
                      >
                        {value}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          color: "rgba(0,0,0,0.6)",
                          fontWeight: 500,
                          mx: 0.5,
                        }}
                      >
                        {getUnitName(unit)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* 活动时间 - 简化版 */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <AccessTimeIcon
                sx={{
                  color: "rgba(0,0,0,0.7)",
                  mr: 1.5,
                  fontSize: "1.2rem",
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.7)",
                    fontSize: "0.9rem",
                    mb: 0.5,
                  }}
                >
                  活动时间
                </Typography>

                <Typography
                  sx={{
                    color: "#000",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  {selectedEvent.displayTime}
                </Typography>
              </Box>
            </Box>

            {/* 活动详情 - 简化版 */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <InfoIcon
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    mr: 1.5,
                    fontSize: "1.2rem",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.7)",
                    fontSize: "0.9rem",
                  }}
                >
                  活动详情
                </Typography>
              </Box>

              <Typography
                sx={{
                  ml: 0.5,
                  color: "rgba(0,0,0,0.7)",
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                }}
              >
                {selectedEvent.description}
              </Typography>
            </Box>

            {/* Zoom会议信息 - 保留框框 */}
            {selectedEvent.zoomlink && selectedEvent.zoompassword && (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid rgba(66,133,244,0.15)",
                  background: "rgba(66,133,244,0.03)",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <VideocamIcon
                    sx={{
                      color: "rgba(66,133,244,0.8)",
                      mr: 1.5,
                      fontSize: "1.2rem",
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "rgba(66,133,244,0.8)",
                      fontSize: "0.9rem",
                    }}
                  >
                    线上会议
                  </Typography>
                </Box>

                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "1rem",
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    ID: {selectedEvent.zoomlink}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    密码: {selectedEvent.zoompassword}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 按钮区域 */}
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}
            >
              <Button
                variant="outlined"
                onClick={() => setSelectedEvent(null)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  borderColor: "rgba(0, 0, 0, 0.2)",
                  color: "rgba(0, 0, 0, 0.7)",
                  "&:hover": {
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                关闭
              </Button>

              <Button
                variant="contained"
                onClick={addToCalendar}
                startIcon={<EventIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                添加到日历
              </Button>
            </Box>
          </Box>
        )}
      </StyledDialog>
    </Box>
  );
};

export default Home;
